import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";
import { getCountryLabel } from "./countries";

const API = process.env.REACT_APP_API_URL || "";

const NAV_KEYS = [
  { key: "nav.home",     to: "/" },
  { key: "nav.analysis", to: "/analysis" },
  { key: "nav.about",    to: "/about" },
  { key: "nav.premium",  to: "/premium", highlight: true },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [subTier, setSubTier] = useState("free");
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { lang, toggleLang, t } = useLanguage();
  const handicap = user?.handicap ?? user?.hcp;
  const countryLabel = user?.country_code
    ? getCountryLabel(user.country_code, lang)
    : "";

  // Fetch credits whenever user changes
  useEffect(() => {
    if (!user?.email || !token) { setCredits(null); setSubTier("free"); return; }
    const refreshSubscription = () => {
      setCredits(user.analysis_credits ?? user.credits ?? user.tokens ?? null);
      fetch(`${API}/api/user/tokens`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(async (r) => {
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || "Could not load credits");
          return data;
        })
        .then((d) => {
          setCredits(d.credits ?? d.tokens ?? null);
          setSubTier(d.subscription_status === "active" ? (d.subscription_tier || "premium") : "free");
        })
        .catch(() => {});
    };

    refreshSubscription();
    window.addEventListener("sm-subscription-updated", refreshSubscription);
    return () => {
      window.removeEventListener("sm-subscription-updated", refreshSubscription);
    };
  }, [user, token]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setUserMenuOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      <Bar
        as={motion.header}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        scrolled={scrolled}
      >
        <Logo to="/">
          SWING<Green>MENTOR</Green>
        </Logo>

        {/* Desktop nav */}
        <DesktopNav>
          {NAV_KEYS.map(({ key, to, highlight }) => (
            <NavLink key={to} to={to} active={location.pathname === to ? 1 : 0} highlight={highlight ? 1 : 0}>
              {t(key)}
            </NavLink>
          ))}
        </DesktopNav>

        {/* Auth area — desktop */}
        <AuthArea>
          <LangToggle onClick={toggleLang}>{lang === "en" ? "RU" : "EN"}</LangToggle>
          {user ? (
            <UserBtn onClick={() => setUserMenuOpen((o) => !o)}>
              <UserAvatar>{(user.first_name || user.name)?.[0]?.toUpperCase() ?? "U"}</UserAvatar>
              <UserName>{user.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : user.name}</UserName>
              <Caret open={userMenuOpen}>▾</Caret>

              <AnimatePresence>
                {userMenuOpen && (
                  <UserDropdown
                    as={motion.div}
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.18 }}
                  >
                    <DropItem>
                      <DropLabel>{t("auth.signedInAs")}</DropLabel>
                      <DropEmail>{user.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : user.name}</DropEmail>
                      <DropEmail style={{ fontSize: "0.72rem", opacity: 0.55 }}>{user.email}</DropEmail>
                    </DropItem>

                    {/* Credits + Plan row */}
                    <DropItem>
                      <DropCreditsRow>
                        <div>
                          <DropLabel>{t("auth.credits")}</DropLabel>
                          <DropCredits warn={credits === 0}>
                            {credits !== null ? credits.toLocaleString() : "—"}
                          </DropCredits>
                        </div>
                        <div>
                          <DropLabel>{t("auth.plan")}</DropLabel>
                          <DropTierBadge tier={subTier}>
                            {subTier === "pro"
                              ? t("auth.planPro")
                              : subTier === "premium"
                              ? t("auth.planPremium")
                              : t("auth.planFree")}
                          </DropTierBadge>
                        </div>
                      </DropCreditsRow>
                    </DropItem>

                    <DropItem>
                      <DropCreditsRow>
                        <div>
                          <DropLabel>{t("auth.handicap")}</DropLabel>
                          <DropValue>
                            {handicap !== undefined && handicap !== null && handicap !== ""
                              ? handicap
                              : "—"}
                          </DropValue>
                        </div>
                        <div>
                          <DropLabel>{t("auth.country")}</DropLabel>
                          <DropValue>{countryLabel || "—"}</DropValue>
                        </div>
                      </DropCreditsRow>
                    </DropItem>
                    <DropDivider />
                    <LogoutBtn onClick={handleLogout}>{t("auth.signOut")}</LogoutBtn>
                  </UserDropdown>
                )}
              </AnimatePresence>
            </UserBtn>
          ) : (
            <AuthButtons>
              <SignInBtn to="/log-in">{t("auth.signIn")}</SignInBtn>
              <SignUpBtn to="/register">{t("auth.signUp")}</SignUpBtn>
            </AuthButtons>
          )}
        </AuthArea>

        {/* Hamburger — mobile */}
        <Burger
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <BurgerBar open={menuOpen} top />
          <BurgerBar open={menuOpen} />
          <BurgerBar open={menuOpen} bottom />
        </Burger>
      </Bar>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <Drawer
            as={motion.nav}
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            {NAV_KEYS.map(({ key, to, highlight }, i) => (
              <motion.div
                key={to}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + i * 0.07 }}
              >
                <DrawerLink to={to} active={location.pathname === to ? 1 : 0} highlight={highlight ? 1 : 0}>
                  {t(key)}
                </DrawerLink>
              </motion.div>
            ))}

            <DrawerDivider />

            {user ? (
              <>
                <DrawerUser>
                  <DrawerAvatar>{(user.first_name || user.name)?.[0]?.toUpperCase() ?? "U"}</DrawerAvatar>
                  <div>
                    <DrawerUserName>{user.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : user.name}</DrawerUserName>
                    <DrawerUserEmail>{user.email}</DrawerUserEmail>
                    <DrawerUserEmail>
                      {t("auth.handicap")}: {handicap ?? "—"} · {t("auth.country")}: {countryLabel || "—"}
                    </DrawerUserEmail>
                  </div>
                </DrawerUser>
                <DrawerLangToggle onClick={toggleLang}>{lang === "en" ? "RU" : "EN"}</DrawerLangToggle>
                <DrawerLogout onClick={handleLogout}>{t("auth.signOut")}</DrawerLogout>
              </>
            ) : (
              <DrawerAuthRow>
                <DrawerLangToggle onClick={toggleLang}>{lang === "en" ? "RU" : "EN"}</DrawerLangToggle>
                <DrawerAuthLink to="/log-in">{t("auth.signIn")}</DrawerAuthLink>
                <DrawerSignUp to="/register">{t("auth.signUp")}</DrawerSignUp>
              </DrawerAuthRow>
            )}
          </Drawer>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {menuOpen && (
          <Scrim
            as={motion.div}
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

/* ── Styled ── */
const Bar = styled.header`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 900;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6vw;
  gap: 1.5rem;
  transition: background 0.3s, backdrop-filter 0.3s, border-color 0.3s;
  background: ${({ scrolled }) => scrolled ? "rgba(255,255,255,0.92)" : "transparent"};
  backdrop-filter: ${({ scrolled }) => scrolled ? "blur(16px)" : "none"};
  border-bottom: 1px solid ${({ scrolled }) => scrolled ? "rgba(0,0,0,0.08)" : "transparent"};
`;

const Logo = styled(Link)`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #111;
  text-decoration: none;
  flex-shrink: 0;
`;

const Green = styled.span`color: #4CAF50;`;

const DesktopNav = styled.nav`
  display: flex;
  gap: 2.5rem;
  flex: 1;
  justify-content: center;

  @media (max-width: 700px) { display: none; }
`;

const NavLink = styled(Link)`
  font-size: 0.7rem;
  font-weight: ${({ highlight }) => highlight ? 700 : 600};
  letter-spacing: 0.2em;
  color: ${({ active, highlight }) => active || highlight ? "#4CAF50" : "rgba(0,0,0,0.65)"};
  text-decoration: none;
  position: relative;
  padding-bottom: 4px;
  transition: color 0.2s;

  &::after {
    content: "";
    position: absolute;
    bottom: 0; left: 0;
    width: ${({ active }) => active ? "100%" : "0"};
    height: 1.5px;
    background: #4CAF50;
    transition: width 0.25s ease;
  }

  &:hover { color: #388E3C; }
  &:hover::after { width: 100%; }
`;

/* Auth area */
const AuthArea = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 700px) { display: none; }
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SignInBtn = styled(Link)`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: rgba(0,0,0,0.55);
  text-decoration: none;
  padding: 0.45rem 0.9rem;
  transition: color 0.2s;

  &:hover { color: #111; }
`;

const SignUpBtn = styled(Link)`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #000;
  text-decoration: none;
  background: #4CAF50;
  padding: 0.45rem 1.1rem;
  border: 2px solid #4CAF50;
  border-radius: 2px;
  transition: background 0.2s, color 0.2s;

  &:hover { background: transparent; color: #4CAF50; }
`;

/* User button */
const UserBtn = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  transition: background 0.2s;

  &:hover { background: rgba(0,0,0,0.05); }
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #4CAF50;
  color: #000;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UserName = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(0,0,0,0.8);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Caret = styled.span`
  font-size: 0.65rem;
  color: rgba(0,0,0,0.35);
  transition: transform 0.2s;
  transform: ${({ open }) => open ? "rotate(180deg)" : "none"};
`;

const UserDropdown = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 200px;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  z-index: 100;
`;

const DropItem = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(0,0,0,0.06);
`;

const DropLabel = styled.div`
  font-size: 0.62rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(0,0,0,0.35);
  margin-bottom: 0.2rem;
`;

const DropEmail = styled.div`
  font-size: 0.82rem;
  color: rgba(0,0,0,0.7);
  word-break: break-all;
`;

const DropValue = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #4CAF50;
`;

const DropCreditsRow = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const DropCredits = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: ${({ warn }) => warn ? "#ff6b6b" : "#4CAF50"};
  line-height: 1;
`;

const DropTierBadge = styled.div`
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.2rem 0.55rem;
  border-radius: 10px;
  background: ${({ tier }) =>
    tier === "premium" ? "rgba(76,175,80,0.15)" :
    tier === "pro"     ? "rgba(33,150,243,0.12)" :
                         "rgba(0,0,0,0.06)"};
  color: ${({ tier }) =>
    tier === "premium" ? "#4CAF50" :
    tier === "pro"     ? "#2196F3" :
                         "rgba(0,0,0,0.4)"};
  border: 1px solid ${({ tier }) =>
    tier === "premium" ? "rgba(76,175,80,0.3)" :
    tier === "pro"     ? "rgba(33,150,243,0.25)" :
                         "rgba(0,0,0,0.1)"};
`;

const DropDivider = styled.div`
  height: 1px;
  background: rgba(0,0,0,0.06);
`;

const LogoutBtn = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  text-align: left;
  background: none;
  border: none;
  font-family: inherit;
  font-size: 0.82rem;
  color: #ff6b6b;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: rgba(220,50,50,0.08); }
`;

/* Hamburger */
const Burger = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1001;

  @media (max-width: 700px) { display: flex; }
`;

const BurgerBar = styled.span`
  display: block;
  width: 100%;
  height: 2px;
  background: #111;
  border-radius: 2px;
  transition: transform 0.28s, opacity 0.28s;
  ${({ open, top }) => open && top ? "transform: translateY(7px) rotate(45deg);" : ""}
  ${({ open, bottom }) => open && bottom ? "transform: translateY(-7px) rotate(-45deg);" : ""}
  ${({ open, top, bottom }) => open && !top && !bottom ? "opacity: 0; transform: scaleX(0);" : ""}
`;

/* Drawer */
const Drawer = styled.nav`
  position: fixed;
  top: 0; right: 0; bottom: 0;
  width: min(280px, 80vw);
  background: #fff;
  border-left: 1px solid rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem;
  gap: 0.5rem;
  z-index: 950;
`;

const DrawerLink = styled(Link)`
  display: block;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: ${({ active, highlight }) => active || highlight ? "#4CAF50" : "#111"};
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0,0,0,0.07);
  transition: color 0.2s;

  &:hover { color: #4CAF50; }
`;

const DrawerDivider = styled.div`
  height: 1px;
  background: rgba(0,0,0,0.07);
  margin: 1rem 0 0.5rem;
`;

const DrawerUser = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
`;

const DrawerAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #4CAF50;
  color: #000;
  font-size: 0.85rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DrawerUserName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #111;
`;

const DrawerUserEmail = styled.div`
  font-size: 0.72rem;
  color: rgba(0,0,0,0.45);
`;

const DrawerLogout = styled.button`
  background: none;
  border: none;
  padding: 0.6rem 0;
  font-family: inherit;
  font-size: 0.85rem;
  color: #ff6b6b;
  cursor: pointer;
  text-align: left;
`;

const DrawerAuthRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const DrawerAuthLink = styled(Link)`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(0,0,0,0.6);
  text-decoration: none;

  &:hover { color: #111; }
`;

const DrawerSignUp = styled(Link)`
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
  text-decoration: none;
  background: #4CAF50;
  padding: 0.65rem 1rem;
  border-radius: 4px;
  text-align: center;
  transition: background 0.2s, color 0.2s;

  &:hover { background: #388E3C; color: #fff; }
`;

const LangToggle = styled.button`
  background: none;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 4px;
  font-family: inherit;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: rgba(0,0,0,0.55);
  padding: 0.3rem 0.6rem;
  cursor: pointer;
  margin-right: 0.75rem;
  transition: border-color 0.2s, color 0.2s;

  &:hover {
    border-color: #4CAF50;
    color: #4CAF50;
  }
`;

const DrawerLangToggle = styled.button`
  background: rgba(76,175,80,0.08);
  border: 1px solid rgba(76,175,80,0.25);
  border-radius: 4px;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #4CAF50;
  padding: 0.45rem 1rem;
  cursor: pointer;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  transition: background 0.2s;

  &:hover { background: rgba(76,175,80,0.15); }
`;

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 940;
  background: rgba(0,0,0,0.6);
`;

export default Header;
